import React, { lazy, Suspense, useState, useEffect, useRef } from "react";
import { Block } from "baseui/block";
import { useStyletron } from "baseui";
import { Input } from "baseui/input";
import { Button } from "baseui/button";
import { Select } from "baseui/select";
import { Tabs, Tab, ORIENTATION } from "baseui/tabs";
import ProgressBar from "components/progress";
import Skeleton from "components/Skeleton";
import Loader from "react-js-loader";
import { useStateWithCallbackLazy } from "use-state-with-callback";
import DefaultAvatar from "assets/images/user-profile.svg";
import NoAssignRolesAvatar from "assets/images/no_assign_avatar.svg";
import DolCompetencies from "../components/dolCompetency/dolCompetencies";
import RoleSpecificActivities from "../components/dolCompetency/roleSpecificActivities";
import { fetchJobRoles } from "services/requests/job_role";
import assignJobRole from "services/requests/assignJobRole";
import { deepCopy } from "pages/DolCriteria/util";
import { useSetState, useTrackedState } from "store/store";

import {
  Basic_info_role,
  Progress,
  EDIT,
  MISSING,
} from "resources/constants/strings.js";
import SearchIcon from "baseui/icon/search";
import {
  COMMON_RADIUS,
  COMMON_BORDER_WIDTH,
  COMMON_BORDER_COLOR,
} from "utils/style";
import { sortAZSelectOptions } from "utils/select";
const TAB_CONTENT_OVERRIDE_STYLE = ({ $theme }) => ({
  paddingLeft: "0px",
  paddingRight: "0px",
  paddingTop: "0px",
  paddingBottom: "0px",
  width: "100%",
});
const TAB_OVERRIDE_STYLE = ({ $theme, $active }) => ({
  paddingRight: "16px",
  paddingTop: "8px",
  paddingBottom: "8px",
  paddingLeft: "16px",
  fontFamily: "Manrope-Medium",
  fontSize: "18px",
  lineHeight: "25px",
  color: $active ? "#FFFFFF" : $theme?.RAColors?.black,
  backgroundColor: $active
    ? $theme?.RAColors?.gray900
    : $theme?.RAColors?.gray100,
  borderRadius: "24px",
  border: $active ? "none" : "2px solid #AFAFAF",
  marginLeft: 0,
  marginRight: 0,
  cursor: "pointer",
  [$theme.mediaQuery.small]: {
    width: "100%",
  },
  [$theme.mediaQuery.medium]: {
    width: "auto",
  },
  [$theme.mediaQuery.large]: {
    width: "auto",
  },
});
const BLOCK_STYLE = (theme) => ({
  backgroundColor: "white",
  borderRadius: "8px",
  padding: "16px",
  border: `1px solid ${theme.RAColors?.gray200}`,
  width: "auto",
  [theme.mediaQuery.small]: {
    width: "100%",
  },
  [theme.mediaQuery.medium]: {
    width: "auto",
  },
  [theme.mediaQuery.large]: {
    width: "auto",
  },
});
const COMMON_TEXT_STYLE = {
  fontSize: "14px",
  lineHeight: "19px",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const ITEM_TITLE_STYLE = (theme) => ({
  fontFamily: "Manrope-Semibold",
  fontSize: "14px",
  lineHeight: "19px",
  color: theme?.RAColors?.gray800,
  paddingBottom: "8px",
});
const EDIT_TEXT_STYLE = (theme) => ({
  fontFamily: "Manrope-Semibold",
  fontSize: "14px",
  lineHeight: "19px",
  cursor: "pointer",
  color: theme?.RAColors?.blue800,
});
const MISSING_TEXT_STYLE = (theme) => ({
  fontFamily: "Manrope-Semibold",
  fontSize: "16px",
  marginTop: "5px",
  lineHeight: "22px",
  marginBottom: "4px",
  color: theme?.RAColors?.red800,
});
const ITEM_CONTENT_STYLE = (theme) => ({
  fontFamily: "Epilogue-Medium",
  fontSize: "28px",
  lineHeight: "40px",
  color: theme?.RAColors?.gray800,
});
const ROLE_NAME_CONTENT_STYLE = (theme) => ({
  fontFamily: "Epilogue-Medium",
  fontSize: "16px",
  lineHeight: "22px",
  color: theme?.RAColors?.gray800,
});
const NAME_STYLE = (theme) => ({
  ...COMMON_TEXT_STYLE,
  fontFamily: "Manrope-Semibold",
  fontSize: "16px",
  fontWeight: 600,
  lineHeight: "22px",
  color: theme.RAColors?.gray800,
});
const EMAIL_STYLE = {
  ...COMMON_TEXT_STYLE,
  fontSize: "12px",
  fontFamily: "Manrope",
  fontWeight: 500,
};
const LEARNERS_MESSAGE_STYLE = (theme) => ({
  fontFamily: "Manrope-medium",
  marginTop: "20px",
  fontSize: "18px",
  fontWeight: "500",
  lineHeight: "25px",
  color: theme.RAColors?.black,
});
const LABEL_STYLE = (theme) => ({
  ...COMMON_TEXT_STYLE,
  fontFamily: "Manrope-medium",
  fontWeight: 500,
  lineHeight: "22px",
  margin: "0 0 9px",
  [theme.mediaQuery.small]: {
    fontSize: "14px",
  },
  [theme.mediaQuery.medium]: {
    fontSize: "16px",
  },
});
const STICKY_SECTION = (theme) => ({
  position: "-webkit-sticky",
  position: "sticky",
  top: 0,
  backgroundColor: "white",
  paddingBottom: "10px",
  [theme.mediaQuery.small]: {
    zIndex: 9999,
  },
  [theme.mediaQuery.medium]: {
    zIndex: 1,
  },
  [theme.mediaQuery.large]: {
    zIndex: 1,
  },
});
const SELECT_OVERRIDE_STYLE = {
  Root: ({ $theme }) => ({ width: "100%" }),
  ControlContainer: ({ $theme, $error }) => ({
    backgroundColor: "none",
    ...COMMON_RADIUS("4px"),
    ...COMMON_BORDER_COLOR(
      $error ? $theme.RAColors?.red600 : $theme.RAColors?.gray200
    ),
    ...COMMON_BORDER_WIDTH("1px"),
    color: $theme.RAColors?.gray900,
  }),
};
const INPUT_OVERRIDE_STYLE = {
  Input: ({ $theme }) => ({
    backgroundColor: "none",
    color: $theme.RAColors?.gray900,
  }),
  Root: ({ $theme }) => ({
    backgroundColor: "none",
    ...COMMON_RADIUS("4px"),
    ...COMMON_BORDER_COLOR($theme.RAColors?.gray200),
    ...COMMON_BORDER_WIDTH("1px"),
    width: "198px",
    height: "48px",
    [$theme.mediaQuery.small]: {
      width: "100%",
    },
    [$theme.mediaQuery.medium]: {
      width: "198px",
    },
    [$theme.mediaQuery.large]: {
      width: "198px",
    },
  }),
  StartEnhancer: ({ $theme }) => ({
    paddingLeft: 0,
    paddingRight: 0,
    backgroundColor: "transparent",
  }),
};
const SAVE_BTN_STYLE = ($theme) => ({
  borderRadius: "10px",
  paddingTop: "14px",
  paddingBottom: "14px",
  paddingLeft: "16px",
  paddingRight: "16px",
  backgroundColor: "#141414",
  borderColor: "#332FF1",
  color: "#FFFFFF",
  ":hover": { backgroundColor: "#141414" },
});
const AVATAR_STYLE = { width: "60px", height: "60px", borderRadius: "100%" };
const NO_ASSIGN_AVATAR_STYLE = {
  width: "14px",
  height: "14px",
};
const SEPRATOR = {
  borderBottom: "1px solid #e0e0e0",
};
const ModalComponent = lazy(() => import("./switchRoles/switchRoles.js"));

const SearchRender = ({ collapse, expand }) => {
  const [css, theme] = useStyletron();
  return (
    <>
      <Block display="flex" gridGap="20px" justifyContent="flex-end">
        <Block
          className={css(EDIT_TEXT_STYLE(theme))}
          data-testid="collapse-all"
          onClick={collapse}
        >
          {"Collapse All"}
        </Block>
        <Block
          className={css(EDIT_TEXT_STYLE(theme))}
          data-testid="expand-all"
          onClick={expand}
        >
          {"Expand All"}
        </Block>
      </Block>
    </>
  );
};

const Summary = ({
  Data,
  changeDolData,
  allLearnerData,
  selectedIndex,
  updateData,
  rolespermission,
  handleClick,
  openVerifyCompetencyModal,
  loader,
}) => {
  const [css, theme] = useStyletron();
  const [row, setRow] = useState(false);
  const [switchRoleRow, setSwitchRoleRow] = useState(false);
  const [activeKey, setActiveKey] = React.useState("0");
  const [jobRole, setJobRole] = useState("");
  const [dolDetails, setDolDetails] = useStateWithCallbackLazy([]);
  const [jobRoleOptions, setJobRoleOptions] = React.useState([]);
  const [assignJobLoader, setAssignJobLoader] = React.useState(false);
  const [searchString, setSearchString] = React.useState("");
  const userEmail = localStorage.getItem("email") || "";
  const [openedId, setOpenedId] = useState("");
  const [isSpecificActivity, setSpecificActivity] = useState(false);
  const setState = useSetState();
  const state = useTrackedState();
  const DolCompetenciesRef = useRef(null);
  const RoleSpecificActivitiesRef = useRef(null);

  useEffect(() => {
    const fetchOption = async () => {
      let jobRoleArray = await getJobRole(userEmail);
      setJobRoleOptions(jobRoleArray);
    };
    fetchOption();
    uniqueDolList = addTaskName(uniqueDolList);
    uniqueDolList = addRoleSpecificActivitiesField(uniqueDolList);
    if (Data) {
      setState((prev) => ({ ...prev, learnerEmail: Data["EmailId"] }));
      setState((prev) => ({
        ...prev,
        learnerName: Data["LastName"] + " " + Data["FirstName"],
      }));
    }

    if (row) {
      if (isSpecificActivity) {
        let result;
        uniqueDolList.RoleSpecificActivities.filter((obj) => {
          if (obj.TaskID === openedId) {
            result = obj;
          }
        });
        setRow(result);
      } else {
        let result;
        uniqueDolList.Category.filter((obj) =>
          obj.Criteria.filter((criteria) => {
            if (criteria.CriteriaID === openedId) {
              result = criteria;
            }
          })
        );
        setRow(result);
      }
    }
    setDolDetails(uniqueDolList);
  }, [Data]);

  let uniqueDolList = Data?.LearnerChecklist;
  // to make the search functionality work with Role Specific Activities
  const addRoleSpecificActivitiesField = (data) => {
    if (
      !data?.RoleSpecificActivities ||
      !Array.isArray(data?.RoleSpecificActivities)
    )
      return data;

    const addedFieldRoleSpecificActivities = data.RoleSpecificActivities.map(
      (item) => ({
        ...item,
        ParentName: "Role Specific Activities",
      })
    );
    return {
      ...data,
      RoleSpecificActivities: addedFieldRoleSpecificActivities,
    };
  };
  const addTaskName = (data) => {
    try {
      for (var obj in data) {
        if (
          data.hasOwnProperty(obj) &&
          typeof data[obj] === "object" &&
          Array.isArray(data[obj]) &&
          data[obj] !== null
        ) {
          data[obj]?.forEach((items) => {
            items?.Criteria?.forEach((criteria) => {
              for (var key in criteria) {
                if (
                  typeof criteria[key] === "object" &&
                  !Array.isArray(criteria[key]) &&
                  criteria[key] !== null
                ) {
                  criteria[key].TaskName = key;
                }
              }

              criteria.isAccordionOpen = !criteria.IsCriteriaCompleted;
            });
            if (obj === "Category") {
              items.isAccordionOpen = !items.IsCatagoryCompleted;
            } else {
              items.isAccordionOpen = !items.IsTaskCompleted;
            }
          });
          //
        }
      }
      return data;
    } catch (e) {
      //console.log(e);
    }
  };
  const searchKeywords = (event) => {
    const searchString = event.target.value;
    setSearchString(searchString);
    let values = deepCopy(uniqueDolList);
    values = addRoleSpecificActivitiesField(values);

    if (searchString !== "") {
      let newCategory = [];
      let newRoleSpecificActivities = [];

      values.Category.forEach((category) => {
        const { Criteria } = category;
        const newCriteria = Criteria.filter((c) =>
          c.CriteriaName.toLowerCase().includes(
            searchString.toLowerCase().trim()
          )
        );

        if (newCriteria.length) {
          newCategory.push({ ...category, Criteria: newCriteria });
        }
      });

      values.RoleSpecificActivities.forEach((activity) => {
        if (
          activity.TaskName.toLowerCase().includes(
            searchString.toLowerCase().trim()
          )
        ) {
          newRoleSpecificActivities.push(activity);
        }
      });

      setDolDetails({
        ...dolDetails,
        Category: newCategory,
        RoleSpecificActivities: newRoleSpecificActivities,
      });
    } else {
      setDolDetails(values);
    }
  };
  const getAvailableTasks = (itemObject) => {
    let array = [
      "RTI",
      "RTIStatus",
      "JS",
      "JSStatus",
      // "OJT",
      // "OJTStatus",
      "MentorProficientStatus",
      "LearnerProficientStatus",
      "FinalApprovalStatus",
    ];
    let availableListedTask = Object.keys(itemObject);

    let filteredAvailableListedTask = array.filter(function (n) {
      return availableListedTask.indexOf(n) !== -1;
    });
    return filteredAvailableListedTask;
  };
  const openModal = (data) => {
    setSwitchRoleRow(data);
  };
  function closeModal() {
    setSwitchRoleRow(false);
    updateData(true);
  }
  const getJobRole = async () => {
    const response = await fetchJobRoles(userEmail, "Published");
    let jobRolesArray = [];
    var newArray = response?.data?.JobRoles?.filter(function (el) {
      return el.RoleName !== "";
    });
    newArray?.forEach((element, index) => {
      let obj = {};
      obj.label = element.RoleName;
      obj.id = index + 1;
      obj.value = element.RoleName;
      obj.index = parseInt(element.Index);
      jobRolesArray.push(obj);
    });
    return [
      ...new Map(jobRolesArray.map((item) => [item["value"], item])).values(),
    ];
  };
  const saveJob = (event, learnerEmail) => {
    event.stopPropagation();
    event.preventDefault();
    if (jobRole !== "") {
      const payload = {
        RoleIndex: jobRole[0].index,
        LearnerEmail: learnerEmail,
      };
      setAssignJobLoader(true);
      assignJobRole(payload, userEmail)
        .then(function (response) {
          if (response.status === 200) {
            setAssignJobLoader(false);
            allLearnerData(learnerEmail);
            changeDolData(learnerEmail, selectedIndex);
          }
        })
        .catch((err) => {
          setAssignJobLoader(false);
          console.log(err);
        });
    }
  };
  const handleCallback = (childData) => {
    if (childData !== Data?.RoleName) {
      changeDolData(state?.learnerEmail, selectedIndex);
    }
  };
  const collapseAll = () => {
    const refValue =
      activeKey === "0" ? DolCompetenciesRef : RoleSpecificActivitiesRef;
    if (refValue.current) refValue.current?.collapseAll();
  };
  const expandAll = () => {
    const refValue =
      activeKey === "0" ? DolCompetenciesRef : RoleSpecificActivitiesRef;
    if (refValue.current) refValue.current?.expandAll();
  };
  //role permission
  const filteredactions = rolespermission?.filter(
    (obj) => obj.Action_name === "Edit Job Role"
  );
  return (
    <Block>
      <Block
        display="flex"
        flexWrap="wrap"
        gridGap="20px"
        alignItems="stretch"
        marginBottom="28px"
      >
        <Block
          display="flex"
          alignItems="center"
          flexDirection="row"
          gridGap="16px"
          className={css({ ...BLOCK_STYLE(theme) })}
        >
          <Block
            as="img"
            src={Data?.ProfileImage || DefaultAvatar}
            alt="User Avatar"
            className={css(AVATAR_STYLE)}
            data-testid="learner-profile-image"
          />
          {loader ? (
            <Skeleton oval={false} width="100px" rows={2} />
          ) : (
            <Block overflow="hidden">
              <Block
                className={css(NAME_STYLE(theme))}
                data-testid="learner-profile-name"
              >
                {Data?.LastName + " " + Data?.FirstName}
              </Block>
              <Block
                className={css(EMAIL_STYLE)}
                data-testid="learner-profile-email"
              >
                {Data?.EmailId}
              </Block>
            </Block>
          )}
        </Block>
        <Block className={css({ ...BLOCK_STYLE(theme) })}>
          <Block
            className={css(ITEM_TITLE_STYLE(theme))}
            data-testid="progress_title"
          >
            {Progress}
          </Block>
          <Block display="flex" gridGap="16px" alignItems="center">
            <Block
              className={css(ITEM_CONTENT_STYLE(theme))}
              data-testid="learner-profile-progress"
            >
              {Data?.LearnerChecklist?.Progress
                ? Data?.LearnerChecklist?.Progress
                : 0}
              %
            </Block>
            <Block width="100px">
              <ProgressBar
                value={Data?.LearnerChecklist?.Progress}
                targetValue={0}
              />
            </Block>
          </Block>
        </Block>
        <Block className={css({ ...BLOCK_STYLE(theme) })}>
          <Block display={"flex"} justifyContent="space-between">
            <Block
              className={css(ITEM_TITLE_STYLE(theme))}
              data-testid="role_title"
            >
              {Basic_info_role}
            </Block>
            {Data?.RoleName &&
              filteredactions &&
              filteredactions[0]?.Edit === "Y" && (
                <Block
                  className={css(EDIT_TEXT_STYLE(theme))}
                  data-testid="role_edit"
                  onClick={() => openModal(Data)}
                >
                  {EDIT}
                </Block>
              )}
          </Block>
          {Data?.RoleName ? (
            <Block display="flex" gridGap="16px" alignItems="center">
              <Block
                className={css(ROLE_NAME_CONTENT_STYLE(theme))}
                marginTop="8px"
                marginBottom="8px"
                data-testid="role-name"
              >
                {Data?.RoleName}
              </Block>
            </Block>
          ) : (
            <Block
              display={"flex"}
              gridGap="12px"
              alignItems="center"
              marginTop="10px"
            >
              <Block
                as="img"
                src={NoAssignRolesAvatar}
                alt="no assigned Avatar"
                className={css(NO_ASSIGN_AVATAR_STYLE)}
              />
              <Block
                className={css(MISSING_TEXT_STYLE(theme))}
                width="100%"
                data-testid="missing-text"
              >
                {MISSING}
              </Block>
            </Block>
          )}
        </Block>
      </Block>
      {!Data?.RoleName && <Block className={css(SEPRATOR)} />}
      {Data?.RoleName ? (
        <Block>
          <Block
            display={"flex"}
            justifyContent="space-between"
            alignItems={"baseline"}
          >
            <Block display="flex" flexDirection="column" width={"100%"}>
              <Block className={css(STICKY_SECTION(theme))}>
                <Block
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gridGap="16px"
                >
                  <Block
                    display="flex"
                    alignItems="center"
                    gridGap="16px"
                    flexWrap="wrap"
                    data-testid="filter-item"
                  >
                    <Block
                      className={css(
                        TAB_OVERRIDE_STYLE({
                          $theme: theme,
                          $active: activeKey === "0",
                        })
                      )}
                      data-testid="dol-active"
                      onClick={() => setActiveKey("0")}
                    >
                      DOL Competencies
                    </Block>
                    <Block
                      className={css(
                        TAB_OVERRIDE_STYLE({
                          $theme: theme,
                          $active: activeKey === "1",
                        })
                      )}
                      data-testid="role-specific-active"
                      onClick={() => setActiveKey("1")}
                    >
                      Role-Specific Activities
                    </Block>
                    <Input
                      value={searchString}
                      onChange={(e) => searchKeywords(e)}
                      placeholder={"Enter keywords"}
                      clearOnEscape
                      name="search_keywords"
                      data-testid="search_text_input"
                      overrides={{
                        Input: { style: INPUT_OVERRIDE_STYLE.Input },
                        Root: { style: INPUT_OVERRIDE_STYLE.Root },
                        StartEnhancer: {
                          style: INPUT_OVERRIDE_STYLE.StartEnhancer,
                        },
                      }}
                      startEnhancer={
                        <SearchIcon size={24} color={theme.RAColors?.gray900} />
                      }
                    />
                  </Block>
                  <SearchRender expand={expandAll} collapse={collapseAll} />
                </Block>
              </Block>
              <Tabs
                onChange={({ activeKey }) => {
                  setActiveKey(activeKey);
                }}
                overrides={{
                  Root: { style: { width: "100%" } },
                  TabBar: {
                    component: (props) => <Block />,
                  },
                  TabContent: {
                    style: TAB_CONTENT_OVERRIDE_STYLE,
                  },
                }}
                activeKey={activeKey}
                orientation={ORIENTATION.horizontal}
                renderAll
              >
                <Tab
                  title={"DOL Competencies"}
                  overrides={{
                    Tab: {
                      style: TAB_OVERRIDE_STYLE,
                    },
                  }}
                >
                  <Block marginTop={"14px"}>
                    <DolCompetencies
                      ref={DolCompetenciesRef}
                      Data={Data}
                      dolDetails={dolDetails}
                      getAvailableTasks={getAvailableTasks}
                      changeDolData={changeDolData}
                      selectedIndex={selectedIndex}
                      rolespermission={rolespermission}
                      handleClick={handleClick}
                      openVerifyCompetencyModal={openVerifyCompetencyModal}
                      loader={loader}
                    />
                  </Block>
                </Tab>
                <Tab
                  title={"Role-Specific Activities"}
                  overrides={{
                    Tab: {
                      style: TAB_OVERRIDE_STYLE,
                    },
                  }}
                >
                  <Block marginTop={"24px"}>
                    <RoleSpecificActivities
                      ref={RoleSpecificActivitiesRef}
                      Data={Data}
                      dolDetails={dolDetails}
                      getAvailableTasks={getAvailableTasks}
                      loader={loader}
                      changeDolData={changeDolData}
                      allLearnerData={allLearnerData}
                      selectedIndex={selectedIndex}
                      rolespermission={rolespermission}
                      handleClick={handleClick}
                      openVerifyCompetencyModal={openVerifyCompetencyModal}
                    />
                  </Block>
                </Tab>
                <Input
                  value={searchString}
                  onChange={(e) => searchKeywords(e)}
                  placeholder={"Enter keywords"}
                  clearOnEscape
                  data-testid="search_text_input"
                  overrides={{
                    Input: { style: INPUT_OVERRIDE_STYLE.Input },
                    Root: { style: INPUT_OVERRIDE_STYLE.Root },
                    StartEnhancer: {
                      style: INPUT_OVERRIDE_STYLE.StartEnhancer,
                    },
                  }}
                  startEnhancer={
                    <SearchIcon size={24} color={theme.RAColors?.gray900} />
                  }
                />
              </Tabs>
            </Block>
          </Block>
        </Block>
      ) : (
        <>
          {filteredactions && filteredactions[0]?.Edit === "Y" && (
            <Block
              height={"600px"}
              display="flex"
              justifyContent={"center"}
              alignItems="center"
              flexDirection={"column"}
            >
              <Block className={css(LEARNERS_MESSAGE_STYLE(theme))}>
                {"There’s no role assigned to this learner"}
              </Block>
              <Block width={"328px"} marginTop="20px" marginBottom="30px">
                <Block as="h5" className={css(LABEL_STYLE(theme))}>
                  {"Assign a job role"}
                </Block>
                <Select
                  clearable={false}
                  searchable={false}
                  options={sortAZSelectOptions(jobRoleOptions)}
                  placeholder="Select a job role"
                  value={jobRole}
                  maxDropdownHeight="300px"
                  onChange={(params) => setJobRole(params.value)}
                  overrides={{
                    Root: { style: SELECT_OVERRIDE_STYLE.Root },
                    ControlContainer: {
                      style: SELECT_OVERRIDE_STYLE.ControlContainer,
                    },
                  }}
                />
              </Block>
              <Block display="flex" alignItems="center">
                <Button
                  type="submit"
                  overrides={{ BaseButton: { style: SAVE_BTN_STYLE(theme) } }}
                  data-testid="save_text"
                  onClick={(event) => saveJob(event, Data?.EmailId)}
                >
                  {"Assign"}
                </Button>
              </Block>
            </Block>
          )}
        </>
      )}
      <Suspense fallback={<div />}>
        {switchRoleRow && (
          <ModalComponent
            row={switchRoleRow}
            changeDolData={changeDolData}
            selectedIndex={selectedIndex}
            close={() => closeModal()}
            parentCallback={handleCallback}
          />
        )}
      </Suspense>
      {assignJobLoader ? (
        <Loader
          type="bubble-loop"
          bgColor={"#332ff0"}
          color={"#332ff0"}
          size={100}
        />
      ) : null}
    </Block>
  );
};
export default Summary;
